import { forms } from '@cwos/api-client';

/**
 * Authoring a form version — the decisions, out of the component.
 *
 * ===========================================================================
 * WHY "EDIT" IS NOT A WORD THIS SURFACE USES
 * ===========================================================================
 * A published version is immutable. `contract.ts` rule 3 is not politeness:
 * the approval record binds to the version's `content_hash`, so changing the
 * wording of a published version would either break its approval or silently
 * relabel evidence somebody already gave. The domain's answer is
 * `draftRevision()` — version n+1, in `draft`, needing its own approval, with n
 * retired only when n+1 publishes.
 *
 * So {@link revisionIntent} exists to make the screen say the true sentence
 * before the press, not after: "this will create v4 as a draft; v3 stays
 * published and the responses already given keep answering it". A button
 * labelled "Edit" over that behaviour is a lie about evidence, and the reason
 * this file, rather than the component, decides the label.
 *
 * ===========================================================================
 * VALIDATION HERE IS A COURTESY, NEVER THE BOUNDARY
 * ===========================================================================
 * `schemas.ts` is the authority and refuses everything below independently.
 * These checks exist so an author reads the rule beside the field instead of
 * meeting it as a 400 after they have typed forty questions — and they are
 * DERIVED from the client's own `fieldTakesOptions` / `operatorTakesValue`
 * rather than re-stating the rule, so there is one sentence of it per side of
 * the wire.
 */

// -------------------------------------------------------------- the draft

/**
 * A field being authored.
 *
 * `id` is a local key for React and for the reducer. It is NOT sent: the server
 * identifies a field by `key`, and inventing a client-side identity that looked
 * like a server one is how a screen starts believing it owns the row.
 */
export interface DraftField {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly dataType: forms.FormFieldType;
  readonly required: boolean;
  /** Raw, comma-free-form text; split at submit. Empty when the type takes none. */
  readonly optionsText: string;
}

export interface DraftSection {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly fields: readonly DraftField[];
}

export interface VersionDraft {
  readonly sections: readonly DraftSection[];
  readonly requiresAttestation: boolean;
  readonly attestationPrompt: string;
  readonly defaultCadence: forms.FormCadence;
}

let seq = 0;
/** A local-only key. Deterministic enough for React, never sent anywhere. */
export function localId(prefix: string): string {
  seq += 1;
  return `${prefix}-${seq}`;
}

export function emptyField(): DraftField {
  return {
    id: localId('field'),
    key: '',
    label: '',
    dataType: 'text',
    required: false,
    optionsText: '',
  };
}

export function emptySection(): DraftSection {
  return { id: localId('section'), key: '', title: '', fields: [emptyField()] };
}

export function emptyDraft(): VersionDraft {
  return {
    sections: [emptySection()],
    requiresAttestation: false,
    attestationPrompt: '',
    defaultCadence: 'none',
  };
}

/**
 * Start a revision FROM THE VERSION BEING REVISED, not from blank.
 *
 * A revision is the same instrument with a change in it, so the author must see
 * what they are changing. Seeding from `FormVersionView.fields` — which are the
 * version's own fields, in its own section and field order — is also what keeps
 * a revision honest: an author who does not retype the questions cannot
 * accidentally drop one.
 */
export function draftFromVersion(version: forms.FormVersionView): VersionDraft {
  const bySection = new Map<
    string,
    { title: string; order: number; fields: forms.FormFieldView[] }
  >();
  for (const field of version.fields) {
    const entry = bySection.get(field.sectionKey) ?? {
      title: field.sectionTitle,
      order: field.sectionOrder,
      fields: [],
    };
    entry.fields.push(field);
    bySection.set(field.sectionKey, entry);
  }
  const sections = [...bySection.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([key, entry]) => ({
      id: localId('section'),
      key,
      title: entry.title,
      fields: [...entry.fields]
        .sort((a, b) => a.fieldOrder - b.fieldOrder)
        .map((f) => ({
          id: localId('field'),
          key: f.fieldKey,
          label: f.label,
          dataType: f.dataType,
          required: f.required,
          optionsText: (f.options ?? []).join('\n'),
        })),
    }));
  return {
    sections: sections.length > 0 ? sections : [emptySection()],
    requiresAttestation: version.requiresAttestation,
    attestationPrompt: version.attestationPrompt ?? '',
    defaultCadence: version.defaultCadence,
  };
}

// ---------------------------------------------------------------- validation

/** One problem, addressed to the thing it is about. */
export interface DraftProblem {
  readonly where: string;
  readonly message: string;
}

const KEY_PATTERN = /^[a-z0-9][a-z0-9_]{0,63}$/;
const FORM_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{1,63}$/;

/** Options text → the list the server takes. Blank lines are not options. */
export function parseOptions(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

export function validateFormKey(formKey: string): string | null {
  if (formKey.trim() === '') return 'A form needs a key — the stable name it keeps forever.';
  return FORM_KEY_PATTERN.test(formKey.trim())
    ? null
    : 'Lower-case letters, digits, hyphens and underscores, 2–64 characters. This is the key, not the title.';
}

/**
 * Everything wrong with this draft, all at once.
 *
 * ALL AT ONCE ON PURPOSE. The server answers with an issue list too, and an
 * author fixing one problem per round-trip through a fifty-question instrument
 * is the experience this exists to avoid.
 */
export function validateDraft(draft: VersionDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (draft.sections.length === 0) {
    problems.push({ where: 'Sections', message: 'A version needs at least one section.' });
  }
  const sectionKeys = new Set<string>();
  const fieldKeys = new Set<string>();
  for (const section of draft.sections) {
    const where = section.title.trim() === '' ? 'A section' : section.title.trim();
    if (section.title.trim() === '') {
      problems.push({ where, message: 'This section needs a title.' });
    }
    if (!KEY_PATTERN.test(section.key.trim())) {
      problems.push({
        where,
        message: 'The section key must be lower snake_case, 1–64 characters.',
      });
    } else if (sectionKeys.has(section.key.trim())) {
      problems.push({ where, message: `Two sections share the key \`${section.key.trim()}\`.` });
    } else {
      sectionKeys.add(section.key.trim());
    }
    if (section.fields.length === 0) {
      problems.push({ where, message: 'A section with no questions asks nobody anything.' });
    }
    for (const field of section.fields) {
      const fieldWhere = field.label.trim() === '' ? `${where} · a question` : field.label.trim();
      if (field.label.trim() === '') {
        problems.push({ where: fieldWhere, message: 'This question needs a label.' });
      }
      if (!KEY_PATTERN.test(field.key.trim())) {
        problems.push({
          where: fieldWhere,
          message: 'The question key must be lower snake_case, 1–64 characters.',
        });
      } else if (fieldKeys.has(field.key.trim())) {
        // Across the WHOLE version, not per section: an answer names a
        // `fieldKey` and nothing else, so two fields sharing one key would make
        // a response ambiguous about which question it answered.
        problems.push({
          where: fieldWhere,
          message: `Two questions share the key \`${field.key.trim()}\`. An answer names only its key, so they would be indistinguishable in the evidence.`,
        });
      } else {
        fieldKeys.add(field.key.trim());
      }
      const options = parseOptions(field.optionsText);
      if (forms.fieldTakesOptions(field.dataType) && options.length === 0) {
        problems.push({
          where: fieldWhere,
          message: 'A choice question must list its choices, one per line.',
        });
      }
      if (!forms.fieldTakesOptions(field.dataType) && options.length > 0) {
        problems.push({
          where: fieldWhere,
          message: 'Only single-choice and multiple-choice questions may list choices.',
        });
      }
    }
  }
  // The server's co-occurrence rule, stated in the author's words. It refuses
  // both halves of the pair independently.
  if (draft.requiresAttestation && draft.attestationPrompt.trim() === '') {
    problems.push({
      where: 'Attestation',
      message: 'An instrument that requires an attestation must say what is being attested.',
    });
  }
  if (!draft.requiresAttestation && draft.attestationPrompt.trim() !== '') {
    problems.push({
      where: 'Attestation',
      message: 'An attestation prompt with the requirement turned off asks nobody anything.',
    });
  }
  return problems;
}

/** The draft as the server's input. Only called when {@link validateDraft} is empty. */
export function toDraftInput(draft: VersionDraft): forms.DraftVersionInput {
  return {
    sections: draft.sections.map((section) => ({
      key: section.key.trim(),
      title: section.title.trim(),
      fields: section.fields.map((field) => {
        const options = parseOptions(field.optionsText);
        return {
          key: field.key.trim(),
          label: field.label.trim(),
          dataType: field.dataType,
          required: field.required,
          ...(forms.fieldTakesOptions(field.dataType) ? { options } : {}),
        };
      }),
    })),
    // Both halves or neither. Sending `requiresAttestation: false` with a
    // leftover prompt is the exact 400 the rule above is written to prevent.
    ...(draft.requiresAttestation
      ? { requiresAttestation: true, attestationPrompt: draft.attestationPrompt.trim() }
      : {}),
    defaultCadence: draft.defaultCadence,
  };
}

// ------------------------------------------------------------- immutability

/**
 * WHAT PRESSING "REVISE" WILL ACTUALLY DO, said before the press.
 *
 * Four arms because the consequence genuinely differs, and the one that matters
 * is `published`: the reader is looking at a version people have already
 * answered, and the thing they are about to do does NOT change it.
 */
export type RevisionIntent =
  | { kind: 'from-published'; from: number; to: number }
  | { kind: 'from-approved'; from: number; to: number }
  | { kind: 'from-draft'; from: number; to: number }
  | { kind: 'from-retired'; from: number; to: number };

export function revisionIntent(version: forms.FormVersionView): RevisionIntent {
  const from = version.versionNumber;
  const to = from + 1;
  switch (version.state) {
    case 'published':
      return { kind: 'from-published', from, to };
    case 'approved':
      return { kind: 'from-approved', from, to };
    case 'retired':
      return { kind: 'from-retired', from, to };
    default:
      return { kind: 'from-draft', from, to };
  }
}

export function revisionSentence(intent: RevisionIntent): string {
  switch (intent.kind) {
    case 'from-published':
      return `This does not change v${intent.from}. It writes v${intent.to} as a new draft, which needs its own approval before it can be published — and v${intent.from} stays in use until it is. Every response already given keeps answering v${intent.from}, with the wording it was given.`;
    case 'from-approved':
      return `v${intent.from} is approved against its exact wording, so it cannot be edited. This writes v${intent.to} as a new draft, and v${intent.to} needs its own approval — the approval of v${intent.from} does not carry over.`;
    case 'from-retired':
      return `v${intent.from} is retired. This writes v${intent.to} as a new draft from its wording; nothing about v${intent.from} or the responses given against it changes.`;
    case 'from-draft':
      return `v${intent.from} is still a draft, so this writes a further draft, v${intent.to}, from its wording. Neither has been approved and neither is in use.`;
  }
}

/** The button's label. Never "Edit" — see the module header. */
export function revisionLabel(intent: RevisionIntent): string {
  return `Revise into v${intent.to}`;
}

// ---------------------------------------------------------------- mappings

/** Everything wrong with a proposed signal mapping, in the author's words. */
export function validateMapping(input: {
  fieldKey: string;
  matchOperator: forms.FormMatchOperator;
  matchValue: string;
  signalSource: forms.FormSignalSource;
  signalKey: string;
}): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (input.fieldKey.trim() === '') {
    problems.push({ where: 'Question', message: 'Choose the question whose answer this watches.' });
  }
  const takesValue = forms.operatorTakesValue(input.matchOperator);
  if (takesValue && input.matchValue.trim() === '') {
    problems.push({ where: 'Match', message: 'This comparison needs a value to compare against.' });
  }
  if (!takesValue && input.matchValue.trim() !== '') {
    problems.push({
      where: 'Match',
      message: '“Is blank” and “is not blank” compare against nothing — clear the value.',
    });
  }
  if (input.signalKey.trim() === '') {
    problems.push({ where: 'Signal', message: 'Name the signal a matching answer should raise.' });
  } else if (
    input.signalSource === 'customer_defined' &&
    !forms.CUSTOM_SIGNAL_KEY_PATTERN.test(input.signalKey.trim())
  ) {
    problems.push({
      where: 'Signal',
      message:
        'A signal your organization invented must be written `custom:something`, so it can never be mistaken for — or collide with — a signal Phibian reasons over.',
    });
  }
  return problems;
}

/**
 * The sentence an approver reads before approving a mapping.
 *
 * A signal mapping is the seam where an answer somebody typed becomes something
 * the platform reasons about, and approving one is therefore a decision about
 * automation, not a visibility toggle. The screen says what will start
 * happening, in the tense it will happen in.
 */
export function mappingApprovalConsequence(mapping: forms.FormSignalMappingView): string {
  const raises =
    mapping.automation === 'raise_task'
      ? 'record the signal AND raise a task for somebody to act on'
      : 'record the signal, and raise nothing';
  return `Approving this means that from now on, an answer to \`${mapping.fieldKey}\` that matches will ${raises}. It is bound to this exact mapping: changing the comparison, the signal or the automation afterwards breaks the approval and the mapping goes inert again until somebody approves the new one.`;
}
